/*
  AuthForm — shared Login/Register form used by both the modal and the
  standalone pages. `mode` is the initial tab, and onDone fires after a
  successful auth so the host can close the modal / redirect.
*/
import { useState } from "react";
import { Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function AuthForm({ initialMode = "login", onDone, onForgot }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        toast.success("Welcome back!");
      } else {
        await register(email.trim(), password, name.trim());
        toast.success("Account created. Your data has been synced to this account.");
      }
      onDone && onDone();
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Something went wrong";
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="auth-form" className="w-full">
      <div className="flex gap-1 p-1 rounded-lg mb-5" style={{ background: "var(--surface-2)" }}>
        <button
          type="button"
          data-testid="auth-tab-login"
          onClick={() => setMode("login")}
          className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
          style={{
            background: mode === "login" ? "var(--surface)" : "transparent",
            color: mode === "login" ? "var(--primary-ink)" : "var(--text-muted)",
            boxShadow: mode === "login" ? "var(--sh-sm)" : "none",
          }}
        >Sign in</button>
        <button
          type="button"
          data-testid="auth-tab-register"
          onClick={() => setMode("register")}
          className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
          style={{
            background: mode === "register" ? "var(--surface)" : "transparent",
            color: mode === "register" ? "var(--primary-ink)" : "var(--text-muted)",
            boxShadow: mode === "register" ? "var(--sh-sm)" : "none",
          }}
        >Create account</button>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        {mode === "register" && (
          <label className="block">
            <span className="label-caps block mb-1">Name <span style={{ color: "var(--text-muted)" }}>(optional)</span></span>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="auth-name-input"
                placeholder="Ada Lovelace"
                className="w-full pl-9 pr-3 h-10 rounded-lg border bg-transparent text-sm"
                style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
              />
            </div>
          </label>
        )}
        <label className="block">
          <span className="label-caps block mb-1">Email</span>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
              autoComplete="email"
              placeholder="you@example.com"
              className="w-full pl-9 pr-3 h-10 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            />
          </div>
        </label>
        <label className="block">
          <span className="label-caps block mb-1">Password</span>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password-input"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder={mode === "register" ? "At least 6 characters" : "Your password"}
              className="w-full pl-9 pr-3 h-10 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            />
          </div>
        </label>

        {err && (
          <div data-testid="auth-error" className="text-sm rounded-lg px-3 py-2"
               style={{ background: "color-mix(in srgb, var(--error) 12%, transparent)", color: "var(--error)" }}>
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="auth-submit"
          className="w-full h-10 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
          style={{ background: "var(--primary)" }}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "login" ? "Sign in" : "Create account"}
        </button>

        {mode === "login" && (
          <button
            type="button"
            onClick={() => onForgot && onForgot(email)}
            data-testid="auth-forgot-link"
            className="block text-xs mx-auto hover:underline pt-1"
            style={{ color: "var(--text-muted)" }}
          >Forgot password?</button>
        )}

        {mode === "register" && (
          <p className="text-xs text-center pt-1" style={{ color: "var(--text-muted)" }}>
            Any data you've already added on this device will be linked to your account automatically.
          </p>
        )}
      </form>
    </div>
  );
}
